import { isBrowser } from "@/app/utilities/isBrowser";
import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Sets and removes event listeners
 * @param event A string which represents the event to listen
 * @param callback The callback function for the event listener
 * @param target Target element (window by default)
 * @param options The event listener options
 */
export function useEventListener<EventType = unknown>(
	event: string,
	callback: (e: EventType) => void,
	target: HTMLElement | Window | MediaQueryList = isBrowser()
		? window
		: (undefined as never),
	options?: boolean | AddEventListenerOptions
): void {
	const targetMemo = useMemo<HTMLElement | Window | MediaQueryList>(
		() => target,
		[target]
	);
	const callbackRef = useRef<(e: EventType) => void>(callback);
	// Create a fixed event listener that calls the latest callback ref without
	// triggering a full re-render.
	const eventListener = useCallback(
		(e: Event) => callbackRef.current(e as EventType),
		[]
	);

	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	useEffect(() => {
		const supportsEventListener = targetMemo && targetMemo.addEventListener;

		if (!supportsEventListener || !callbackRef.current) return;

		targetMemo.addEventListener(event, eventListener, options);

		return (): void => {
			targetMemo.removeEventListener(event, eventListener, options);
		};
	}, [event, eventListener, options, targetMemo]);
}
